/*@flow*/
/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import VegaLiteFlex from 'comps/App/VegaLite/Flex';
import ColumnGrid from '../ColumnGrid';
import { useState, useContext, useEffect } from 'react';
import useCartoMap from 'lib/hooks/useCartoMap';
import Category from '../Category';
import Blank from 'lib/Blank';
import { extractVectorTiles } from 'lib/getCartoTiles';
import { ChartDeckContext } from '../contexts';
import Present from 'lib/Present';
import keepNonEmptyKeys from 'lib/keepNonEmptyKeys';
import { type VectorTileUrls, type CartoResponse } from 'lib/getCartoMap';
import hash from 'lib/hash';
import ClearButton from '../ClearButton';
import ChartContainer from '../ChartContainer';
import * as I from 'immutable';

const QUERY_TMPL = () => `
  WITH busiest_streets AS (
    SELECT the_geom_webmercator, score, street, start_date
    FROM carmera_pedestrian_count_pivoted
  )
  SELECT * FROM busiest_streets
`;

const colorDomain = [0, 60, 120, 180, 240, 300];
const colorRange = ['#f3e79b', '#fab27f', '#eb7f86', '#b95e9a', '#5c53a5'];
const colorZipped = I.fromJS(colorDomain)
  .zip(I.fromJS(colorRange))
  .toJS();

type onSelectT = (?{ [key: string]: string[] }) => any;

const VEGA_LITE_SPEC = {
  $schema: 'https://vega.github.io/schema/vega-lite/v3.json',
  mark: 'rect',

  autosize: 'fit',
  padding: { left: 10, top: 10, right: 0, bottom: 10 },

  selection: {
    streetSelection: {
      type: 'multi',
      encodings: ['x', 'y'],
    },
  },

  encoding: {
    x: {
      field: 'street',
      type: 'nominal',
      axis: {
        labels: false,
        ticks: false,
        title: 'Street',
      },
    },
    y: {
      field: 'start_date',
      type: 'nominal',
      timeUnit: 'monthyear',
      title: 'Month',
    },
    color: {
      condition: {
        selection: 'streetSelection',
        field: 'score',
        aggregate: 'max',
        type: 'quantitative',
        legend: {
          title: 'Score',
        },
        scale: {
          domain: colorDomain,
          range: colorRange,
        },
      },
      value: 'grey',
    },
  },
};

const PedestrianActivityByStreetOverTime = (props: {
  onSelect: onSelectT,
  mapConfig: ?CartoResponse,
  analysisId: ?string,
}) => {
  const { onSelect, mapConfig, analysisId } = props;
  return (
    <ChartContainer mapConfig={mapConfig} analysisId={analysisId}>
      {({ data }) => {
        return (
          <VegaLiteFlex
            signals={{
              onSignalStreetSelection_tuple(_, payload) {
                if (Blank.is(payload)) {
                  onSelect && onSelect();
                }
              },
              onSignalStreetSelection(
                _: string,
                payload: {
                  monthyear_start_date?: Date[],
                  street?: string[],
                }
              ) {
                if (Present.is(payload) && onSelect) {
                  if (payload.monthyear_start_date && payload.street) {
                    const { street } = payload;
                    onSelect({ streets: street });
                  }
                }
              },
            }}
            data={{ values: data }}
            spec={VEGA_LITE_SPEC}
          />
        );
      }}
    </ChartContainer>
  );
};

const DATAVIEW_1 = 'carmera-streets-widget';
const ANALYSIS_1 = 'carmera-data';
const LAYER_1 = 'carmera-streets';

const INIT_CONFIG = {
  buffersize: {
    mvt: 0,
  },
  analyses: [
    {
      id: ANALYSIS_1,
      type: 'source',
      params: {
        query: QUERY_TMPL(),
      },
    },
  ],
  layers: [
    {
      id: LAYER_1,
      type: 'cartodb',
      options: {
        source: {
          id: ANALYSIS_1,
        },
      },
    },
  ],
  dataviews: {
    [DATAVIEW_1]: {
      type: 'aggregation',
      source: {
        id: ANALYSIS_1,
      },
      options: {
        aggregation: 'max',
        column: 'street',
        aggregationColumn: 'score',
      },
    },
  },
};

const getMapboxTiles = ({
  mapTiles,
  cartoLayerName,
}: {
  mapTiles: VectorTileUrls,
  cartoLayerName: string,
}) => [
  {
    id: `${cartoLayerName}-source`,
    type: 'source',
    config: {
      type: 'vector',
      tiles: mapTiles,
      maxzoom: 18,
    },
  },
  {
    id: `${cartoLayerName}-layer`,
    type: 'layer',
    config: {
      type: 'line',
      source: `${cartoLayerName}-source`,
      'source-layer': cartoLayerName,
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': [
          'let',
          'numeric_score',
          ['to-number', ['get', 'score']],
          [
            'case',
            ...colorZipped
              .map(v => [['<', ['var', 'numeric_score'], v[0]], v[1]])
              .reduce((s, v) => s.concat(v), []),
            'white',
          ],
        ],
        'line-width': ['step', ['zoom'], 2, 15, 6],
      },
    },
  },
];

export default (props: { name: string }) => {
  const [params, setParams] = useState({});
  const [showLayers, setShowLayers] = useState(false);
  const mapConfig = useCartoMap(params, INIT_CONFIG);
  const mapTiles = mapConfig ? extractVectorTiles(mapConfig) : null;
  const { addMapTiles, removeMapTiles } = useContext(ChartDeckContext);

  useEffect(() => {
    if (mapTiles) {
      const tiles = getMapboxTiles({
        mapTiles,
        cartoLayerName: LAYER_1,
      });
      if (showLayers) {
        addMapTiles(tiles);
      } else {
        removeMapTiles(tiles);
      }
    }
  }, [mapTiles && mapTiles[0], showLayers, hash(params)]);

  useEffect(() => {
    setTimeout(() => Present.is(params.filters) && setShowLayers(true), 100);
  }, [hash(params.filters)]);

  return (
    <div>
      <p css={css('text-align: justify;')}>
        CARMERA Pedestrian Scores are based on street-level observations from
        CARMERA's city-wide, vehicular sensor network. Scores are derived using
        statistical normalization and adjustment factors to reflect empirically
        measured foot traffic patterns under a uniform methodology.
      </p>
      <div
        css={css(
          `
      display: grid;
      grid-template-rows: 2rem 1fr;
      grid-column-gap: 10px;
      `
        )}
      >
        <div css={css('padding-top: 5px; padding-bottom: 5px;')}>
          <label css={css('margin: 0; cursor: pointer;')}>
            <input
              type="checkbox"
              checked={showLayers}
              onChange={e => setShowLayers(e.target.checked)}
            />{' '}
            Show map layers
          </label>
          <ClearButton
            filters={params.filters}
            onClick={() => {
              setParams({
                filters: {},
              });
            }}
          />
        </div>
        <ColumnGrid columns="calc(100% - 320px) 300px" padBottom="20px">
          <div css={css('height: 300px; width: 95%;')}>
            <PedestrianActivityByStreetOverTime
              mapConfig={mapConfig}
              analysisId={ANALYSIS_1}
              onSelect={selection => {
                if (selection) {
                  const { streets } = selection;
                  const filters = keepNonEmptyKeys({
                    accept: streets,
                  });

                  setParams({
                    filters: {
                      dataviews: {
                        [DATAVIEW_1]: filters,
                      },
                    },
                  });
                }
              }}
            />
          </div>
          <div>
            <Category
              title="Streets"
              id={DATAVIEW_1}
              mapConfig={mapConfig}
              onFiltersChanged={({ accepted }) => {
                const filters = keepNonEmptyKeys({
                  accept: accepted,
                  reject: [],
                });
                if (Present.is(filters)) {
                  setParams({
                    filters: {
                      dataviews: {
                        [DATAVIEW_1]: filters,
                      },
                    },
                  });
                } else if (Present.is(params)) {
                  setParams({
                    filters: {},
                  });
                }
              }}
            />
          </div>
        </ColumnGrid>
      </div>
    </div>
  );
};
